import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  IndianRupee, 
  TrendingUp, 
  LayoutGrid,
  FileCheck,
  Receipt,
  Wallet,
  Settings,
  LogOut,
  Plus,
  Calendar,
  Filter,
  User,
  ChevronDown,
  FileSpreadsheet
} from 'lucide-react';
import './Dashboard.css';
import { getInvoices, getCompanies, getProducts, deleteProduct, deleteCompany, getPaymentSheetHistory } from '../services/api';
import { Invoice, Company } from '../types';
import InvoiceFormModal from './InvoiceFormModal';
import AddProductModal from './AddProductModal';
import AddCompanyModal from './AddCompanyModal';
import EditProductModal from './EditProductModal';
import EditCompanyModal from './EditCompanyModal';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import PaymentSheetModal from './PaymentSheetModel';

interface DashboardStats {
  totalBills: number;
  pendingBills: number;
  totalAmount: number;
  averageAmount: number;
  paidPercentage: number;
  pendingPercentage: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [activeSection, setActiveSection] = useState<'overview' | 'create-invoice' | 'invoices' | 'products' | 'businesses' | 'payment-sheet' | 'settings'>('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('All Time');
  const [selectedBusiness, setSelectedBusiness] = useState('All Businesses');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState('All Months');
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [paymentSheetHistory, setPaymentSheetHistory] = useState<any[]>([]);
  const [productCount, setProductCount] = useState(0);
  const [stats, setStats] = useState<DashboardStats>({
    totalBills: 0,
    pendingBills: 0,
    totalAmount: 0,
    averageAmount: 0,
    paidPercentage: 0,
    pendingPercentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showPaymentSheetModel, setShowPaymentSheetModel] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const periods = React.useMemo(() => ['Today', 'This Week', 'This Month', 'This Quarter', 'This Year', 'Custom Date Range', 'By Year', 'All Time'], []);
  const months = React.useMemo(() => ['All Months', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], []);
  const years = React.useMemo(() => Array.from({length: 10}, (_, i) => (new Date().getFullYear() - i).toString()), []);

  // Normalize any date format to DD.MM.YYYY
  const normalizeDate = React.useCallback((dateValue: any): string => {
    if (!dateValue) return '';
    
    try {
      // If already in DD.MM.YYYY format, validate and return
      if (typeof dateValue === 'string' && dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [day, month, year] = dateValue.split('.').map(Number);
        const testDate = new Date(year, month - 1, day);
        if (testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
          return dateValue; // Already valid DD.MM.YYYY
        }
      }
      
      // Parse ISO date string (YYYY-MM-DD format) - use UTC to avoid timezone issues
      if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        const [year, month, day] = dateValue.split('T')[0].split('-').map(Number);
        return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
      }
      
      // Try to parse as Date object (handles timestamps, etc.)
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        // Use UTC methods to avoid timezone conversion
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}.${month}.${year}`;
      }
    } catch (error) {
      // Silent error handling
    }
    
    return ''; // Return empty string for invalid dates
  }, []);

  // Parse DD.MM.YYYY format to Date object
  const parseInvoiceDate = React.useCallback((dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    try {
      if (typeof dateValue === 'string') {
        // Handle DD.MM.YYYY format
        if (dateValue.includes('.') && !dateValue.includes('T')) {
          const parts = dateValue.split('.');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);
            
            // Validate date components
            if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year > 1900 && year < 2100) {
              const date = new Date(year, month - 1, day);
              // Double-check the date is valid (handles invalid dates like Feb 31)
              if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
                return date;
              }
            }
          }
        }
        // Handle ISO format or other standard formats
        else {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      } else if (dateValue instanceof Date) {
        if (!isNaN(dateValue.getTime())) {
          return dateValue;
        }
      }
    } catch (error) {
      // Silent error handling for production
    }
    
    return null;
  }, []);

  // Define filter function
  const filterInvoicesByPeriodAndBusiness = React.useCallback((invoicesData: Invoice[]) => {
    let filtered = [...invoicesData];

    // Filter by business
    if (selectedBusiness !== 'All Businesses') {
      filtered = filtered.filter(invoice => invoice.company.name === selectedBusiness);
    }

    // Filter by period
    const now = new Date();
    
    filtered = filtered.filter(invoice => {
      const invoiceDate = parseInvoiceDate(invoice.invoiceDate);
      
      // If date is invalid, exclude from filtering
      if (!invoiceDate) {
        return false;
      }
      
      // Normalize dates for comparison (remove time component)
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());
      const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (selectedPeriod) {
        case 'Today':
          return invoiceDateOnly.getTime() === todayOnly.getTime();
          
        case 'This Week':
          const weekAgo = new Date(todayOnly.getTime() - 7 * 24 * 60 * 60 * 1000);
          return invoiceDateOnly >= weekAgo && invoiceDateOnly <= todayOnly;
          
        case 'This Month':
          return invoiceDate.getMonth() === now.getMonth() && 
                 invoiceDate.getFullYear() === now.getFullYear();
                 
        case 'This Quarter':
          const quarter = Math.floor(now.getMonth() / 3);
          const invoiceQuarter = Math.floor(invoiceDate.getMonth() / 3);
          return invoiceQuarter === quarter && 
                 invoiceDate.getFullYear() === now.getFullYear();
                 
        case 'This Year':
          return invoiceDate.getFullYear() === now.getFullYear();
          
        case 'Custom Date Range':
          if (!customStartDate || !customEndDate) return true;
          const startDate = new Date(customStartDate);
          const endDate = new Date(customEndDate);
          const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          return invoiceDateOnly >= startDateOnly && invoiceDateOnly <= endDateOnly;
          
        case 'By Year':
          const yearMatch = invoiceDate.getFullYear().toString() === selectedYear;
          let monthMatch = true;
          if (selectedMonth !== 'All Months') {
            const monthIndex = months.indexOf(selectedMonth) - 1;
            monthMatch = invoiceDate.getMonth() === monthIndex;
          }
          return yearMatch && monthMatch;
          
        case 'All Time':
        default:
          return true;
      }
    });
    
    return filtered;
  }, [selectedPeriod, selectedBusiness, customStartDate, customEndDate, selectedYear, selectedMonth, months, parseInvoiceDate]);

  // Define calculateStats second (depends on filter function)
  const calculateStats = React.useCallback((invoicesData: Invoice[]) => {
    let filteredInvoices = filterInvoicesByPeriodAndBusiness(invoicesData);
    
    // Total Bills = Filtered invoices count (based on selected period and business)
    const totalBills = filteredInvoices.length;
    
    // Total Amount = Sum of all invoice amounts
    const totalAmount = filteredInvoices.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce((itemSum, item) => {
        const itemPrice = item.product.price * item.quantity;
        const discountAmount = itemPrice * (item.discount / 100);
        const afterDiscount = itemPrice - discountAmount;
        const gstAmount = afterDiscount * (item.product.gstRate / 100);
        const itemTotal = afterDiscount + gstAmount;
        
        return itemSum + itemTotal;
      }, 0);
      return sum + invoiceTotal;
    }, 0);
    
    const pendingBills = Math.floor(totalBills * 0.3);
    const averageAmount = totalBills > 0 ? totalAmount / totalBills : 0;
    const paidPercentage = totalBills > 0 ? ((totalBills - pendingBills) / totalBills) * 100 : 0;
    const pendingPercentage = totalBills > 0 ? (pendingBills / totalBills) * 100 : 0;

    setStats({
      totalBills,
      pendingBills,
      totalAmount,
      averageAmount,
      paidPercentage,
      pendingPercentage
    });
  }, [filterInvoicesByPeriodAndBusiness]);

  // Define fetchData third (depends on calculateStats)
  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      
      let invoicesData = [];
      let companiesData = [];
      let productsData = [];
      let paymentHistoryData = [];

      // Fetch from backend only - no localStorage fallback
      try {
        const rawInvoices = await getInvoices();
        
        // Normalize all dates to DD.MM.YYYY format
        invoicesData = rawInvoices.map((inv: any) => ({
          ...inv,
          invoiceDate: normalizeDate(inv.invoiceDate),
          orderDate: normalizeDate(inv.orderDate)
        }));
      } catch (error) {
        invoicesData = [];
      }
      
      try {
        companiesData = await getCompanies();
      } catch (error) {
        companiesData = [];
      }
      
      try {
        productsData = await getProducts();
      } catch (error) {
        productsData = [];
      }

      try {
        paymentHistoryData = await getPaymentSheetHistory();
      } catch (error) {
        paymentHistoryData = [];
      }

      setInvoices(invoicesData);
      setCompanies(companiesData);
      setProducts(productsData);
      setProductCount(productsData.length);
      setPaymentSheetHistory(paymentHistoryData);
      calculateStats(invoicesData);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  }, [calculateStats, normalizeDate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    fetchData();
  }, [selectedPeriod, selectedBusiness, selectedYear, selectedMonth, customStartDate, customEndDate, fetchData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };



  const handleGeneratePDF = async (invoices: Invoice[]) => {
    try {
      // Generate PDF
      await generateInvoicePDF(invoices);
      
      // Save invoices to backend
      const savePromises = invoices.map(invoice => 
        fetch(`${process.env.REACT_APP_API_URL}/invoices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(invoice)
        })
      );
      
      const responses = await Promise.all(savePromises);
      
      // Check if all saves were successful
      const failedSaves = responses.filter(r => !r.ok);
      if (failedSaves.length > 0) {
        throw new Error(`Failed to save ${failedSaves.length} invoices to backend`);
      }
      
      // Close modal
      setShowInvoiceModal(false);
      
      // Refresh dashboard data to show new invoices
      await fetchData();
      
      // Show success message
      alert(`Successfully generated and saved ${invoices.length} invoices!`);

    } catch (error) {
      alert('Error generating or saving invoices. Please try again.');
    }
  };

  const handleProductAdded = () => {
    fetchData();
  };

  const handleCompanyAdded = () => {
    fetchData();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };



  const getRecentInvoices = () => {
    try {
      return filterInvoicesByPeriodAndBusiness(invoices)
        .sort((a, b) => {
          const dateA = parseInvoiceDate(a.invoiceDate);
          const dateB = parseInvoiceDate(b.invoiceDate);
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          return dateB.getTime() - dateA.getTime();
        });
    } catch (error) {
      return [];
    }
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <h1>QR</h1>
        </div>

        <nav className="nav-menu">
          <div className="nav-section">
            <div className="nav-section-title">
              <Receipt size={16} />
              <span>INVOICE GENERATOR</span>
            </div>
            <ul className="nav-list">
              <li 
                className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveSection('overview')}
              >
                <LayoutGrid size={18} />
                <span>Overview</span>
              </li>
              <li 
                className={`nav-item ${activeSection === 'create-invoice' ? 'active' : ''}`}
                onClick={() => {
                  setShowInvoiceModal(true);
                  setActiveSection('create-invoice');
                }}
              >
                <Plus size={18} />
                <span>Issue Invoice</span>
              </li>
            </ul>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">
              <Wallet size={16} />
              <span>BUSINESS MANAGER</span>
            </div>
            <ul className="nav-list">
              <li 
                className={`nav-item ${activeSection === 'invoices' ? 'active' : ''}`}
                onClick={() => setActiveSection('invoices')}
              >
                <LayoutGrid size={18} />
                <span>My Invoices</span>
              </li>
              <li 
                className={`nav-item ${activeSection === 'businesses' ? 'active' : ''}`}
                onClick={() => setActiveSection('businesses')}
              >
                <FileCheck size={18} />
                <span>My Businesses</span>
              </li>
              <li 
                className={`nav-item ${activeSection === 'products' ? 'active' : ''}`}
                onClick={() => setActiveSection('products')}
              >
                <Receipt size={18} />
                <span>Products</span>
              </li>
              <li 
                className={`nav-item ${activeSection === 'payment-sheet' ? 'active' : ''}`}
                onClick={()=> setActiveSection("payment-sheet")}
                >
                <FileSpreadsheet size={18} />
                <span>Payment Sheet</span>
              </li>
            </ul>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info-section">
            <div className="user-avatar-circle">
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <div className="user-name-text">{user.name || 'User'}</div>
              <div className="user-email-text">{user.email || ''}</div>
            </div>
          </div>
          <button 
            className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveSection('settings')}
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>
          <button className="nav-item" onClick={handleLogout}>
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className="header">
          <button 
            className="btn-create"
            onClick={() => setShowInvoiceModal(true)}
          >
            <Plus size={18} />
            Create Invoice
          </button>
          <div className="user-profile">
            <User size={20} />
            <span>{user.name || 'User'}</span>
          </div>
        </header>

        {/* Page Title and Filters */}
        <div className="page-header">
          <h2>
            {(activeSection === 'overview' || activeSection === 'create-invoice') && 'Overview'}
            {activeSection === 'invoices' && 'My Invoices'}
            {activeSection === 'products' && 'Products'}
            {activeSection === 'businesses' && 'My Businesses'}
            {activeSection === 'payment-sheet' && 'Payment Sheet'}
            {activeSection === 'settings' && 'Settings'}
          </h2>
          <div className="filters">
            <div className="filter-dropdown">
              <button 
                className="filter-btn"
                onClick={() => {
                  setShowPeriodDropdown(!showPeriodDropdown);
                  setShowBusinessDropdown(false);
                }}
              >
                <Calendar size={16} />
                {selectedPeriod}
                <ChevronDown size={16} />
              </button>
              {showPeriodDropdown && (
                <div className="dropdown-menu">
                  {periods.map(period => (
                    <button
                      key={period}
                      className={`dropdown-item ${selectedPeriod === period ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedPeriod(period);
                        setShowPeriodDropdown(false);
                        if (period === 'Custom Date Range') {
                          setShowDateRangeModal(true);
                        }
                      }}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Year and Month filters for 'By Year' selection */}
            {selectedPeriod === 'By Year' && (
              <>
                <div className="filter-dropdown">
                  <select 
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="filter-select"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-dropdown">
                  <select 
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="filter-select"
                  >
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            
            <div className="filter-dropdown">
              <button 
                className="filter-btn"
                onClick={() => {
                  setShowBusinessDropdown(!showBusinessDropdown);
                  setShowPeriodDropdown(false);
                }}
              >
                <Filter size={16} />
                {selectedBusiness}
                <ChevronDown size={16} />
              </button>
              {showBusinessDropdown && (
                <div className="dropdown-menu">
                  <button
                    className={`dropdown-item ${selectedBusiness === 'All Businesses' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedBusiness('All Businesses');
                      setShowBusinessDropdown(false);
                    }}
                  >
                    All Businesses
                  </button>
                  {companies.map(company => (
                    <button
                      key={company._id || company.id}
                      className={`dropdown-item ${selectedBusiness === company.name ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBusiness(company.name);
                        setShowBusinessDropdown(false);
                      }}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <>
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FileText size={20} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Bills</p>
              <h3 className="stat-value">{loading ? '...' : stats.totalBills}</h3>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <IndianRupee size={20} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Amount</p>
              <h3 className="stat-value">{loading ? '...' : formatCurrency(stats.totalAmount)}</h3>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp size={20} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Products</p>
              <h3 className="stat-value">{loading ? '...' : productCount}</h3>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FileCheck size={20} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Companies</p>
              <h3 className="stat-value">{loading ? '...' : companies.length}</h3>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="content-grid">
          <div className="content-section">
            <h3>Recent Bills</h3>
            {loading ? (
              <div className="empty-state">
                <p>Loading...</p>
              </div>
            ) : getRecentInvoices().length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <p>No bills yet</p>
                <button 
                  className="empty-action-btn"
                  onClick={() => setShowInvoiceModal(true)}
                >
                  Create Your First Invoice
                </button>
              </div>
            ) : (
              <div className="data-table" style={{ marginTop: '16px' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>SKU</th>
                      <th style={{ textAlign: 'center' }}>Bills</th>
                      <th style={{ textAlign: 'right' }}>Revenue (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Group invoices by SKU
                      const skuData: { [key: string]: { pages: number; revenue: number } } = {};
                      
                      getRecentInvoices().forEach((invoice) => {
                        invoice.items.forEach((item) => {
                          const sku = item.product.sku;
                          const itemPrice = item.product.price * item.quantity;
                          const discountAmount = itemPrice * (item.discount / 100);
                          const afterDiscount = itemPrice - discountAmount;
                          const gstAmount = afterDiscount * (item.product.gstRate / 100);
                          const itemTotal = afterDiscount + gstAmount;
                          
                          if (!skuData[sku]) {
                            skuData[sku] = { pages: 0, revenue: 0 };
                          }
                          skuData[sku].pages += 1;
                          skuData[sku].revenue += itemTotal;
                        });
                      });
                      
                      // Sort by pages (descending)
                      const sortedSkus = Object.entries(skuData)
                        .sort((a, b) => b[1].pages - a[1].pages)
                        .slice(0, 10); // Show top 10
                      
                      return sortedSkus.map(([sku, data]) => (
                        <tr key={sku}>
                          <td style={{ fontWeight: '500' }}>{sku}</td>
                          <td style={{ textAlign: 'center', color: '#06b6d4', fontWeight: '600' }}>
                            {data.pages}
                          </td>
                          <td style={{ textAlign: 'right', color: '#10b981', fontWeight: '600' }}>
                            {formatCurrency(data.revenue)}
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#0369a1' }}>Total Bills Generated:</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#06b6d4' }}>
                    {getRecentInvoices().reduce((sum, invoice) => sum + invoice.items.length, 0)}
                  </span>
                </div>
              </div>
            )}
          </div>


        </div>
        </>
        )}

        {/* Products Section */}
        {activeSection === 'products' && (() => {
          // Filter products by selected business
          const filteredProducts = selectedBusiness === 'All Businesses'
            ? products
            : products.filter(p => {
                const company = companies.find(c => (c._id || c.id) === p.companyId);
                return company?.name === selectedBusiness;
              });

          return (
          <div className="data-section">
            <div className="section-header" style={{ justifyContent: 'flex-start', gap: '16px' }}>
              <button 
                className="add-btn"
                onClick={() => setShowProductModal(true)}
                style={{ order: -1 }}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Add Product
              </button>
              <h2 style={{ margin: 0 }}>Products Management</h2>
            </div>

              {loading ? (
                <div className="data-loading">Loading products...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="data-empty">
                  <Receipt size={48} />
                  <p>No products found for {selectedBusiness}</p>
                  <button onClick={() => setShowProductModal(true)} className="btn-empty-action">
                    Add Product
                  </button>
                </div>
              ) : (
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product Name</th>
                        <th>Company</th>
                        <th>Store</th>
                        <th>Price</th>
                        <th>GST</th>
                        <th>Sizes</th>
                        <th>Colors</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => {
                        const company = companies.find(c => (c._id || c.id) === product.companyId);
                        return (
                          <tr key={product.sku + product.companyId}>
                            <td className="sku-cell">{product.sku}</td>
                            <td className="name-cell">{product.name}</td>
                            <td className="company-cell">{company?.name || 'N/A'}</td>
                            <td className="store-cell">{product.storeName || 'N/A'}</td>
                            <td className="price-cell">₹{product.price}</td>
                            <td className="gst-cell">{product.gstRate}%</td>
                            <td className="sizes-cell">{product.sizes.join(', ')}</td>
                            <td className="colors-cell">{product.colors.join(', ')}</td>
                            <td className="actions-cell">
                              <button 
                                className="btn-edit"
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setShowEditProductModal(true);
                                }}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn-delete"
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this product?')) {
                                    try {
                                      await deleteProduct(product._id || product.id);
                                      alert('Product deleted successfully!');
                                      fetchData();
                                    } catch (err: any) {
                                      alert(err.message || 'Failed to delete product');
                                    }
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* Businesses Section */}
        {activeSection === 'businesses' && (
          <div className="data-section">
            <div className="section-header" style={{ justifyContent: 'flex-start', gap: '16px' }}>
              <button 
                className="add-btn"
                onClick={() => setShowCompanyModal(true)}
                style={{ order: -1 }}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Add Company
              </button>
              <h2 style={{ margin: 0 }}>Companies Management</h2>
            </div>

            {loading ? (
              <div className="data-loading">Loading companies...</div>
            ) : companies.length === 0 ? (
              <div className="data-empty">
                <FileCheck size={48} />
                <p>No companies found</p>
                <button onClick={() => setShowCompanyModal(true)} className="btn-empty-action">
                  Add Company
                </button>
              </div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Seller Name</th>
                      <th>Seller ID</th>
                      <th>GSTIN</th>
                      <th>Phone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies
                      .filter(company => selectedBusiness === 'All Businesses' || company.name === selectedBusiness)
                      .map((company) => (
                      <tr key={company._id || company.id}>
                        <td>{company.name}</td>
                        <td>{company.sellerName || 'N/A'}</td>
                        <td>{company.companyId || 'N/A'}</td>
                        <td>{company.gstin}</td>
                        <td>{company.phone}</td>
                        <td className="actions-cell">
                          <button 
                            className="btn-edit"
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowEditCompanyModal(true);
                            }}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this company?')) {
                                try {
                                  await deleteCompany(company._id || company.id || '');
                                  alert('Company deleted successfully!');
                                  fetchData();
                                } catch (err: any) {
                                  alert(err.message || 'Failed to delete company');
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payment Sheet Section */}
        {activeSection === "payment-sheet" && (
          <div className="data-section">
            <div className="section-header" style={{ justifyContent: 'flex-start', gap: '16px' }}>
              <button
                className="add-btn"
                onClick={() => setShowPaymentSheetModel(true)}
                style={{ order: -1 }}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Generate Payment Sheet
              </button>
              <h2 style={{ margin: 0 }}>Payment Sheet History</h2>
            </div>

            {loading ? (
              <div className="data-loading">Loading payment sheets...</div>
            ) : paymentSheetHistory.length === 0 ? (
              <div className="data-empty">
                <FileSpreadsheet size={48} />
                <p>No payment sheets found</p>
                <button
                  onClick={() => setShowPaymentSheetModel(true)}
                  className="btn-empty-action"
                >
                  Generate Your First Payment Sheet
                </button>
              </div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date Range</th>
                      <th>Total</th>
                      <th>Performance Metrics</th>
                      <th>Cost Analysis</th>
                      <th>Payment Details</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentSheetHistory?.map((sheet) => {
                      const startDate = new Date(sheet.startDate).toLocaleDateString('en-IN');
                      const endDate = new Date(sheet.endDate).toLocaleDateString('en-IN');
                      const createdAt = new Date(sheet.createdAt).toLocaleDateString('en-IN');

                      return (
                        <tr key={sheet._id}>
                          <td className="date-range-cell">
                            <div className="date-range">
                              <div className="date-label">From</div>
                              <div className="date-value">{startDate}</div>
                              <div className="date-label">To</div>
                              <div className="date-value">{endDate}</div>
                              <div className="created-date">Generated: {createdAt}</div>
                            </div>
                          </td>

                          <td>
                            <div className="total-amount">
                              ₹{sheet.totalAmount} 
                            </div>
                          </td>

                          <td className="metrics-cell">
                            <div className="metrics-grid">
                              <div className="metric-item">
                                <span className="metric-label">Delivered</span>
                                <span className="metric-value info">₹{sheet.deliveredAmount}</span>
                                <span className="metric-value success">{sheet.deliveredPercent}% ({sheet.deliveredCount} Orders)</span>
                              </div>
                              <div className="metric-item">
                                <span className="metric-label">RTO</span>
                                <span className="metric-value info">₹{sheet.rtoAmount}</span>
                                <span className="metric-value warning">{sheet.rtoPercent}% ({sheet.rtoCount} Orders)</span>
                              </div>
                              <div className="metric-item">
                                <span className="metric-label">Return</span>
                                <span className="metric-value info">₹{sheet.returnAmount}</span>
                                <span className="metric-value danger">{sheet.returnPercent}% ({sheet.returnCount} Orders)</span>
                              </div>
                            </div>
                          </td>

                          <td className="cost-cell">
                            <div className="cost-details">
                              <div className="cost-item">
                                <span className="cost-label">Ads</span>
                                <div className="cost-info">
                                  <span className="cost-count">{sheet.noAds} campaigns</span>
                                  <span className="cost-amount">₹{sheet.totalAdsCost.toLocaleString()}</span>
                                </div>
                              </div>
                              <div className="cost-item">
                                <span className="cost-label">Referrals</span>
                                <div className="cost-info">
                                  <span className="cost-count">{sheet.noReferral} referrals</span>
                                  <span className="cost-amount">₹{sheet.totalReferralCost.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="payment-cell">
                            <div className="payment-details">
                              <div className="payment-item">
                                <span className="payment-label">Payment Days</span>
                                <span className="payment-value">{sheet.paymentDiffDays} days</span>
                              </div>
                              <div className="payment-item">
                                <span className="payment-label">Settlement</span>
                                <span className="payment-value">{sheet.settlementDiffPercent}%</span>
                              </div>
                            </div>
                          </td>

                          <td className="actions-cell">
                            <button
                              className="btn-download"
                              onClick={() => window.open(sheet.cloudinaryUrl, '_blank')}
                            >
                              <FileSpreadsheet size={16} />
                              Download Excel
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Other Placeholder Sections */}
        {(activeSection === 'invoices' || activeSection === 'settings') && (
          <div className="placeholder-section">
            <div className="placeholder-content">
              <FileText size={64} />
              <h3>Coming Soon</h3>
              <p>This feature is under development</p>
              <button 
                className="back-btn"
                onClick={() => setActiveSection('overview')}
              >
                Back to Overview
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Custom Date Range Modal */}
      {showDateRangeModal && (
        <div className="modal-overlay" onClick={() => setShowDateRangeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Date Range</h3>
              <button 
                className="modal-close"
                onClick={() => setShowDateRangeModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="startDate">Start Date:</label>
                <input
                  type="date"
                  id="startDate"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate">End Date:</label>
                <input
                  type="date"
                  id="endDate"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDateRangeModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowDateRangeModal(false);
                  // Trigger data refresh
                  fetchData();
                }}
                disabled={!customStartDate || !customEndDate}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <InvoiceFormModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setActiveSection('overview');
        }}
        onGeneratePDF={handleGeneratePDF}
      />
      <AddProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSuccess={handleProductAdded}
      />
      <AddCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSuccess={handleCompanyAdded}
      />
      <EditProductModal
        isOpen={showEditProductModal}
        onClose={() => {
          setShowEditProductModal(false);
          setSelectedProduct(null);
        }}
        onSuccess={() => {
          fetchData();
          setShowEditProductModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />
      <EditCompanyModal
        isOpen={showEditCompanyModal}
        onClose={() => {
          setShowEditCompanyModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={() => {
          fetchData();
          setShowEditCompanyModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
      />
      <PaymentSheetModal
        isOpen={showPaymentSheetModel}
        onClose={() => {
          setShowPaymentSheetModel(false);
        }}
      />
    </div>
  );
};

export default Dashboard;
